import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMobileAuth } from "../MobileAuthContext";
import { Phone, ArrowRight, Shield, RefreshCw } from "lucide-react";
import axios from "axios";
import { RememberDevice } from "../../components/RememberDevice";
import { CLUB_LOGO } from "../../club-site/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  parent: "Γονεας / Κηδεμονας",
  coach: "Προπονητης",
  player: "Παικτης",
  management: "Διοικηση",
  fan: "Φιλος της Λευτεριας",
};

const MobileLoginPage = ({returnTo}) => {
  const { login } = useMobileAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone, otp, fan_register, choose_role
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [debugOtp, setDebugOtp] = useState("");
  const [roleDetected, setRoleDetected] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [fanForm, setFanForm] = useState({ first_name: "", last_name: "", birthday: "" });
  const [identities, setIdentities] = useState([]);  // multi-role choice
  const [pendingCode, setPendingCode] = useState("");
  const [rememberDays, setRememberDays] = useState(null);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendTimer]);

  const handleRequestOtp = async () => {
    if (!phone.trim()) {
      setError("Εισαγετε τον αριθμο τηλεφωνου");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/mobile/auth/request-otp`, { phone: phone.trim() });
      setStep("otp");
      setRoleDetected(res.data.role_detected);
      setResendTimer(60);
      if (res.data.simulated && res.data.otp_debug) {
        setDebugOtp(res.data.otp_debug);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Σφαλμα αποστολης OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code, requestedRole = null) => {
    if (code.length !== 6) return;
    setLoading(true);
    setError("");
    try {
      const payload = { phone: phone.trim(), code, remember_days: rememberDays };
      if (requestedRole) payload.role = requestedRole;
      const res = await axios.post(`${API}/mobile/auth/verify-otp`, payload);

      // Multi-role identity picker
      if (res.data?.multi_role) {
        setIdentities(res.data.identities || []);
        setPendingCode(code);
        setStep("choose_role");
        setLoading(false);
        return;
      }

      login(res.data.token, res.data.user, res.data.available_roles || []);
      navigate(returnTo && /^\/app(?:[/?]|$)/.test(returnTo) && !/[\\\r\n]/.test(returnTo) ? returnTo : "/app", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Σφάλμα επαλήθευσης. Δοκιμάστε ξανά.");
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const chooseIdentity = (role) => {
    if (!pendingCode) return;
    handleVerifyOtp(pendingCode, role);
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    const code = newOtp.join("");
    if (code.length === 6) {
      handleVerifyOtp(code);
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pasted[i] || "";
    }
    setOtp(newOtp);
    if (pasted.length === 6) {
      handleVerifyOtp(pasted);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative overflow-hidden" data-testid="mobile-login">
      {/* Background ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#F5A623]/[0.08] blur-3xl" />
        <div className="absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-emerald-500/[0.06] blur-3xl" />
      </div>

      {/* Status bar spacer */}
      <div className="h-[env(safe-area-inset-top,0px)]" />

      <div className="flex-1 flex flex-col items-center justify-center px-6 pb-10 relative z-10">
        {/* Logo + slogan */}
        <div className="mb-10 text-center animate-[fade-in_0.7s_ease-out]">
          <img
            src={CLUB_LOGO}
            alt="ΛΕΥΤΕΡΙΑ FC"
            className="w-32 h-32 mx-auto mb-5 drop-shadow-[0_8px_24px_rgba(245,166,35,0.25)]"
          />
          <h1 className="font-['Bebas_Neue'] text-4xl text-white tracking-[0.08em]">ΛΕΥΤΕΡΙΑ FC</h1>
          <p className="text-[#F5A623] text-xs uppercase tracking-[0.4em] mt-2 font-semibold">
            Μία Ομάδα <span className="text-zinc-600">•</span> Ένα Όνειρο
          </p>
        </div>

        {step === "phone" ? (
          <div className="w-full max-w-sm space-y-5" data-testid="phone-step">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Αριθμος Τηλεφωνου</label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleRequestOtp()}
                  placeholder="+357 99 123456"
                  className="w-full bg-[#161616] border border-[#2a2a2a] rounded-xl px-10 py-3.5 text-white text-lg placeholder-zinc-600 focus:border-[#F5A623] focus:outline-none transition-colors"
                  autoFocus
                  data-testid="phone-input"
                />
              </div>
              <p className="text-xs text-zinc-600 mt-2">
                Εισαγετε τον αριθμο που ειναι καταχωρημενος στην Λευτερια FC
              </p>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <RememberDevice idPrefix="mobile-phone" value={rememberDays} onChange={setRememberDays} disabled={loading} />
            <button
              onClick={handleRequestOtp}
              disabled={loading}
              className="w-full bg-[#F5A623] text-black font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#e09620] transition-colors disabled:opacity-50"
              data-testid="request-otp-btn"
            >
              {loading ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <>Αποστολη Κωδικου <ArrowRight size={18} /></>
              )}
            </button>

            <div className="text-center pt-4 border-t border-[#1a1a1a]">
              <p className="text-zinc-500 text-xs">Δεν είσαι μέλος της Λευτεριά FC;</p>
              <button
                onClick={() => { setStep("fan_register"); setError(""); }}
                className="text-[#F5A623] text-sm font-semibold mt-1 hover:text-[#e09620]"
                data-testid="fan-register-link"
              >
                Εγγράψου Τώρα →
              </button>
            </div>
          </div>
        ) : step === "fan_register" ? (
          <div className="w-full max-w-sm space-y-5" data-testid="fan-register-step">
            <div className="text-center">
              <h2 className="text-white text-xl font-medium">Γίνε Φίλος της Λευτεριάς</h2>
              <p className="text-zinc-500 text-sm mt-1">Νέα, αγώνες, προβλέψεις & περισσότερα</p>
            </div>
            {error && <div data-testid="fan-register-error" role="alert" className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm">{error}</div>}
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Όνομα *</label>
              <input
                value={fanForm.first_name}
                onChange={e => setFanForm(f => ({ ...f, first_name: e.target.value }))}
                placeholder="π.χ. Γιώργος"
                className="w-full bg-[#1a1a1a] border border-[#262626] text-white px-4 py-3 rounded-xl focus:border-[#F5A623] outline-none"
                data-testid="fan-first-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Επώνυμο *</label>
              <input
                value={fanForm.last_name}
                onChange={e => setFanForm(f => ({ ...f, last_name: e.target.value }))}
                placeholder="π.χ. Παπαδόπουλος"
                className="w-full bg-[#1a1a1a] border border-[#262626] text-white px-4 py-3 rounded-xl focus:border-[#F5A623] outline-none"
                data-testid="fan-last-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Τηλέφωνο *</label>
              <div className="relative">
                <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+357 99 123456"
                  className="w-full bg-[#1a1a1a] border border-[#262626] text-white pl-12 pr-4 py-3 rounded-xl focus:border-[#F5A623] outline-none"
                  data-testid="fan-phone-input"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1.5">Ημερομηνία Γέννησης *</label>
              <input
                type="date"
                value={fanForm.birthday}
                onChange={e => setFanForm(f => ({ ...f, birthday: e.target.value }))}
                className="w-full bg-[#1a1a1a] border border-[#262626] text-white px-4 py-3 rounded-xl focus:border-[#F5A623] outline-none"
                data-testid="fan-birthday-input"
              />
            </div>
            <button
              onClick={async () => {
                const first = fanForm.first_name.trim();
                const last = fanForm.last_name.trim();
                if (!first || !last || !phone.trim() || !fanForm.birthday) {
                  setError("Συμπληρώστε όνομα, επώνυμο, τηλέφωνο και ημερομηνία γέννησης");
                  return;
                }
                setLoading(true); setError("");
                try {
                  const res = await axios.post(`${API}/mobile/auth/register-fan`, {
                    first_name: first,
                    last_name: last,
                    name: `${first} ${last}`,
                    phone: phone.trim(),
                    birthday: fanForm.birthday,
                  });
                  setStep("otp");
                  setRoleDetected("fan");
                  setResendTimer(60);
                  if (res.data.simulated && res.data.otp_debug) setDebugOtp(res.data.otp_debug);
                } catch (e) {
                  setError(e.response?.data?.detail || "Σφάλμα εγγραφής");
                } finally { setLoading(false); }
              }}
              disabled={loading}
              className="w-full bg-[#F5A623] text-black font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#e09620] disabled:opacity-50"
              data-testid="fan-register-submit"
            >
              {loading ? <RefreshCw size={18} className="animate-spin" /> : <>Εγγραφή <ArrowRight size={18} /></>}
            </button>
            <button onClick={() => { setStep("phone"); setError(""); }} className="w-full text-zinc-500 text-sm hover:text-white" data-testid="fan-back-btn">
              ← Πίσω στη Σύνδεση
            </button>
          </div>
        ) : (
          <div className={`w-full max-w-sm space-y-5 ${step === "choose_role" ? "hidden" : ""}`} data-testid="otp-step">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#F5A623]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={24} className="text-[#F5A623]" />
              </div>
              <h2 className="text-white text-lg font-medium">Εισαγετε τον κωδικο</h2>
              <p className="text-zinc-500 text-sm mt-1">
                Σταλθηκε στο <span className="text-white">{phone}</span>
              </p>
              {roleDetected && (
                <span className="inline-block mt-2 text-xs bg-[#F5A623]/10 text-[#F5A623] px-3 py-1 rounded-full">
                  {ROLE_LABELS[roleDetected] || roleDetected}
                </span>
              )}
            </div>

            <RememberDevice idPrefix="mobile-otp" value={rememberDays} onChange={setRememberDays} disabled={loading} />
            {/* OTP Input */}
            <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (inputRefs.current[i] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  disabled={loading}
                  className={`w-12 min-w-0 flex-shrink h-14 text-center text-xl font-bold bg-[#161616] border rounded-xl text-white focus:outline-none transition-colors ${
                    digit ? "border-[#F5A623]" : "border-[#2a2a2a] focus:border-[#F5A623]"
                  }`}
                  autoFocus={i === 0}
                  data-testid={`otp-digit-${i}`}
                />
              ))}
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm text-center" data-testid="otp-error">
                {error}
              </div>
            )}

            {loading && (
              <div className="flex justify-center">
                <RefreshCw size={20} className="animate-spin text-[#F5A623]" />
              </div>
            )}

            {/* Debug OTP display — only in simulated mode (Twilio not configured).
                Clearly framed so admins know to set up SMS credentials. */}
            {debugOtp && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-center space-y-1.5" data-testid="debug-otp">
                <p className="text-[10px] text-amber-400 uppercase tracking-wider font-semibold">⚠ SMS μη ρυθμισμενο</p>
                <p className="text-xs text-amber-200/90 leading-tight">Ο κωδικος δεν εσταλη με SMS. Ο διαχειριστης πρεπει να συμπληρωσει τα στοιχεια Twilio στο Admin → SMS / OTP.</p>
                <p className="text-2xl font-mono text-amber-300 tracking-[0.3em] pt-1">{debugOtp}</p>
                <p className="text-[10px] text-amber-500/70">Κωδικός δοκιμής (μόνο στο δοκιμαστικό περιβάλλον)</p>
              </div>
            )}

            <div className="flex justify-between items-center text-sm">
              <button
                onClick={() => { setStep("phone"); setOtp(["","","","","",""]); setError(""); setDebugOtp(""); }}
                className="text-zinc-400 hover:text-white transition-colors"
                data-testid="back-to-phone"
              >
                Αλλαγη αριθμου
              </button>
              {resendTimer > 0 ? (
                <span className="text-zinc-600">Νεος κωδικος σε {resendTimer}s</span>
              ) : (
                <button
                  onClick={handleRequestOtp}
                  className="text-[#F5A623] hover:text-[#e09620] transition-colors"
                  data-testid="resend-otp"
                >
                  Αποστολη ξανα
                </button>
              )}
            </div>
          </div>
        )}

        {/* Multi-role identity picker */}
        {step === "choose_role" && (
          <div className="w-full max-w-sm space-y-5" data-testid="choose-role-step">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#F5A623]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={24} className="text-[#F5A623]" />
              </div>
              <h2 className="text-white text-lg font-medium">Πως θελεις να συνδεθεις;</h2>
              <p className="text-zinc-500 text-sm mt-1">
                Ο αριθμος σου ειναι καταχωρημενος σε πολλους ρολους
              </p>
            </div>

            <RememberDevice idPrefix="mobile-role" value={rememberDays} onChange={setRememberDays} disabled={loading} />
            {error && <p role="alert" data-testid="choose-role-error" className="text-sm text-red-400">{error}</p>}
            <div className="space-y-2.5">
              {identities.map((id) => (
                <button
                  key={id.role}
                  onClick={() => chooseIdentity(id.role)}
                  disabled={loading}
                  className="w-full bg-[#161616] hover:bg-[#1f1f1f] border border-[#2a2a2a] hover:border-[#F5A623] rounded-xl px-4 py-4 flex items-center justify-between text-left transition-colors disabled:opacity-50"
                  data-testid={`choose-role-${id.role}`}
                >
                  <div>
                    <p className="text-white text-sm font-semibold">{ROLE_LABELS[id.role] || id.role}</p>
                    <p className="text-zinc-500 text-xs mt-0.5">{id.name}</p>
                  </div>
                  <ArrowRight size={18} className="text-[#F5A623]" />
                </button>
              ))}
            </div>

            <button
              onClick={() => { setStep("otp"); setIdentities([]); setPendingCode(""); setError(""); }}
              className="w-full text-zinc-500 text-sm hover:text-white"
              data-testid="choose-role-back"
            >
              ← Πισω
            </button>
          </div>
        )}
      </div>

      {/* Bottom branding */}
      <div className="text-center pb-6 text-xs text-zinc-700 relative z-10">
        ΛΕΥΤΕΡΙΑ FC © {new Date().getFullYear()}
      </div>
    </div>
  );
};

export default MobileLoginPage;
